import React, { useState, useMemo, useCallback } from "react";
import { ZoomIn, ZoomOut, RefreshCw, ChevronLeft, ChevronRight, X } from "lucide-react";
import {
  LineChart,
  Line as ChartLine,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Brush,
} from "recharts";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Tooltip as UiTooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

interface BalanceChartProps {
  data: Array<{ date: string; balance: number }>;
  dailyPayment?: number;
}

const BalanceChart: React.FC<BalanceChartProps> = ({ data, dailyPayment }) => {
  const [startIndex, setStartIndex] = useState(0);
  const [endIndex, setEndIndex] = useState(data.length - 1);
  const isZoomed = startIndex > 0 || endIndex < data.length - 1;

  const [showCurrentBalance, setShowCurrentBalance] = useState(true);
  const [showTheoreticalBalance, setShowTheoreticalBalance] = useState(true);

  const [expandedOpen, setExpandedOpen] = useState(false);

  const chartData = useMemo(() => {
    if (!dailyPayment || dailyPayment <= 0) {
      return data;
    }
    return data.map((d) => ({
      ...d,
      theoreticalBalance: d.balance - dailyPayment,
    }));
  }, [data, dailyPayment]);

  const visibleData = useMemo(() => {
    return chartData.slice(startIndex, endIndex + 1);
  }, [chartData, startIndex, endIndex]);

  const { yMin, yMax, theoreticalZeroPosition } = useMemo(() => {
    const currentBalances = visibleData.map((d) => d.balance);
    const theoreticalBalances =
      dailyPayment && dailyPayment > 0
        ? visibleData.map(
            (d) => (d as { theoreticalBalance?: number }).theoreticalBalance as number
          )
        : [];

    const allBalances = [...currentBalances, ...theoreticalBalances];
    const minBalance = Math.min(...allBalances);
    const maxBalance = Math.max(...allBalances);
    const range = maxBalance - minBalance || Math.abs(maxBalance) || 1000;
    const padding = range * 0.2;
    const yMinCalc = Math.floor((minBalance - padding) / 100) * 100;
    const yMaxCalc = Math.ceil((maxBalance + padding) / 100) * 100;

    let theoreticalZeroPosCalc = 50;
    if (theoreticalBalances.length > 0) {
      const theoMin = Math.min(...theoreticalBalances);
      const theoMax = Math.max(...theoreticalBalances);
      if (theoMax <= 0) {
        theoreticalZeroPosCalc = 0;
      } else if (theoMin >= 0) {
        theoreticalZeroPosCalc = 100;
      } else {
        theoreticalZeroPosCalc = ((theoMax - 0) / (theoMax - theoMin)) * 100;
      }
    }

    return { yMin: yMinCalc, yMax: yMaxCalc, theoreticalZeroPosition: theoreticalZeroPosCalc };
  }, [visibleData, dailyPayment]);

  const handleZoomIn = useCallback(() => {
    const currentRange = endIndex - startIndex;
    if (currentRange <= 7) return;
    const newRange = Math.max(7, Math.floor(currentRange * 0.5));
    const center = Math.floor((startIndex + endIndex) / 2);
    const newStart = Math.max(0, center - Math.floor(newRange / 2));
    const newEnd = Math.min(data.length - 1, newStart + newRange);
    setStartIndex(newStart);
    setEndIndex(newEnd);
  }, [startIndex, endIndex, data.length]);

  const handleZoomOut = useCallback(() => {
    const currentRange = endIndex - startIndex;
    const newRange = Math.min(data.length - 1, currentRange * 2);
    const center = Math.floor((startIndex + endIndex) / 2);
    const newStart = Math.max(0, center - Math.floor(newRange / 2));
    const newEnd = Math.min(data.length - 1, newStart + newRange);
    setStartIndex(newStart);
    setEndIndex(Math.min(data.length - 1, newEnd));
  }, [startIndex, endIndex, data.length]);

  const handlePanLeft = useCallback(() => {
    const currentRange = endIndex - startIndex;
    const panAmount = Math.max(1, Math.floor(currentRange * 0.2));
    const newStart = Math.max(0, startIndex - panAmount);
    const newEnd = newStart + currentRange;
    setStartIndex(newStart);
    setEndIndex(Math.min(data.length - 1, newEnd));
  }, [startIndex, endIndex, data.length]);

  const handlePanRight = useCallback(() => {
    const currentRange = endIndex - startIndex;
    const panAmount = Math.max(1, Math.floor(currentRange * 0.2));
    const newEnd = Math.min(data.length - 1, endIndex + panAmount);
    const newStart = newEnd - currentRange;
    setStartIndex(Math.max(0, newStart));
    setEndIndex(newEnd);
  }, [startIndex, endIndex, data.length]);

  const handleReset = useCallback(() => {
    setStartIndex(0);
    setEndIndex(data.length - 1);
  }, [data.length]);

  const handleBrushChange = useCallback((brushData: { startIndex?: number; endIndex?: number }) => {
    if (brushData && brushData.startIndex !== undefined && brushData.endIndex !== undefined) {
      setStartIndex(brushData.startIndex);
      setEndIndex(brushData.endIndex);
    }
  }, []);

  const parseYMD = (dateStr: string) => {
    const [y, m, d] = dateStr.split("-").map(Number);
    return { year: y, month: m, day: d };
  };

  const formatXAxis = (dateStr: string) => {
    const { month, day } = parseYMD(dateStr);
    return `${month}/${day}`;
  };

  const formatCurrency = (value: number) => {
    if (value >= 1000 || value <= -1000) {
      return `$${(value / 1000).toFixed(1)}k`;
    }
    return `$${value.toFixed(0)}`;
  };

  const CustomTooltip = ({
    active,
    payload,
    label,
  }: {
    active?: boolean;
    payload?: Array<{ dataKey?: string; value?: number }>;
    label?: string;
  }) => {
    if (active && payload && payload.length && label) {
      const { year, month, day } = parseYMD(label);

      const formattedDate = `${new Date(year, month - 1, day).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })}`;
      const currentBalance = payload.find((p) => p.dataKey === "balance")?.value;
      const theoreticalBalance = payload.find((p) => p.dataKey === "theoreticalBalance")?.value;

      return (
        <div className="rounded-md border border-border bg-card p-2 text-xs shadow-md">
          <span className="block font-semibold">{formattedDate}</span>
          <span className="block text-[#1976d2]">
            Current: ${currentBalance?.toLocaleString("en-US", { minimumFractionDigits: 2 })}
          </span>
          {theoreticalBalance !== undefined && (
            <span
              className={cn("block", theoreticalBalance >= 0 ? "text-green-600" : "text-red-600")}
            >
              With Offer: $
              {theoreticalBalance?.toLocaleString("en-US", { minimumFractionDigits: 2 })}
            </span>
          )}
        </div>
      );
    }
    return null;
  };

  const chartBlock = (height: number, gradientId: string) => (
    <div className="w-full" style={{ height }}>
      <ResponsiveContainer>
        <LineChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 25 }}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#4caf50" />
              <stop offset={`${theoreticalZeroPosition}%`} stopColor="#4caf50" />
              <stop offset={`${theoreticalZeroPosition}%`} stopColor="#f44336" />
              <stop offset="100%" stopColor="#f44336" />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
          <XAxis
            dataKey="date"
            tickFormatter={formatXAxis}
            tick={{ fontSize: 10 }}
            interval="preserveStartEnd"
            minTickGap={30}
          />
          <YAxis
            domain={[yMin, yMax]}
            tickFormatter={formatCurrency}
            tick={{ fontSize: 10 }}
            width={50}
          />
          <Tooltip content={<CustomTooltip />} />
          <ReferenceLine y={0} stroke="#666" strokeDasharray="3 3" />
          {showCurrentBalance && (
            <ChartLine
              type="monotone"
              dataKey="balance"
              stroke="#1976d2"
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={false}
              activeDot={{ r: 4, fill: "#1976d2" }}
              name="Current"
            />
          )}
          {dailyPayment && dailyPayment > 0 && showTheoreticalBalance && (
            <ChartLine
              type="monotone"
              dataKey="theoreticalBalance"
              stroke={`url(#${gradientId})`}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
              name="With Offer"
            />
          )}
          <Brush
            dataKey="date"
            height={20}
            stroke="#1976d2"
            startIndex={startIndex}
            endIndex={endIndex}
            onChange={handleBrushChange}
            tickFormatter={formatXAxis}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );

  const iconBtn = (
    label: string,
    onClick: () => void,
    disabled: boolean,
    children: React.ReactNode
  ) => (
    <UiTooltip>
      <TooltipTrigger asChild>
        <span>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0 p-1"
            onClick={(e) => {
              e.stopPropagation();
              onClick();
            }}
            disabled={disabled}
          >
            {children}
          </Button>
        </span>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </UiTooltip>
  );

  return (
    <TooltipProvider delayDuration={300}>
      <div className="mt-6 cursor-pointer" onClick={() => setExpandedOpen(true)}>
        <div className="mb-1 flex items-center justify-between">
          <div className="flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
            {iconBtn(
              "Pan Left",
              handlePanLeft,
              startIndex === 0,
              <ChevronLeft className="h-4 w-4" />
            )}
            {iconBtn("Zoom Out", handleZoomOut, !isZoomed, <ZoomOut className="h-4 w-4" />)}
            {iconBtn("Reset View", handleReset, !isZoomed, <RefreshCw className="h-4 w-4" />)}
            {iconBtn(
              "Zoom In",
              handleZoomIn,
              endIndex - startIndex <= 7,
              <ZoomIn className="h-4 w-4" />
            )}
            {iconBtn(
              "Pan Right",
              handlePanRight,
              endIndex === data.length - 1,
              <ChevronRight className="h-4 w-4" />
            )}
          </div>
        </div>

        {chartBlock(220, "theoreticalGradient")}

        <div className="mt-0.5 flex flex-wrap justify-center gap-6">
          <button
            type="button"
            className={cn(
              "flex select-none items-center gap-2 transition-opacity",
              showCurrentBalance ? "opacity-100" : "opacity-40 hover:opacity-60"
            )}
            onClick={(e) => {
              e.stopPropagation();
              setShowCurrentBalance(!showCurrentBalance);
            }}
          >
            <span className="inline-block w-5 border-t-2 border-dashed border-[#1976d2]" />
            <span className={cn("text-xs", !showCurrentBalance && "line-through")}>
              Current Balance
            </span>
          </button>
          {dailyPayment && dailyPayment > 0 && (
            <button
              type="button"
              className={cn(
                "flex select-none items-center gap-2 transition-opacity",
                showTheoreticalBalance ? "opacity-100" : "opacity-40 hover:opacity-60"
              )}
              onClick={(e) => {
                e.stopPropagation();
                setShowTheoreticalBalance(!showTheoreticalBalance);
              }}
            >
              <span className="h-0.5 w-5 bg-gradient-to-r from-green-600 to-red-600" />
              <span className={cn("text-xs", !showTheoreticalBalance && "line-through")}>
                With Offer (${dailyPayment.toLocaleString()}/day)
              </span>
            </button>
          )}
        </div>
      </div>

      <Dialog open={expandedOpen} onOpenChange={setExpandedOpen}>
        <DialogContent
          className="max-w-4xl [&>button.absolute]:hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <DialogHeader className="flex flex-row items-center justify-between space-y-0 pr-10 text-left">
            <DialogTitle>Daily Balance</DialogTitle>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => setExpandedOpen(false)}
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </Button>
          </DialogHeader>
          {chartBlock(400, "theoreticalGradientExpanded")}
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
};

export default BalanceChart;
